/**
 * AccessBoard — "My Access". The personal face of the access agent: what I can
 * reach, with which role, WHY, and my pending requests. Board-first — this IS
 * the agent's surface, not a chat transcript.
 *
 * Presentational only: it renders a fully-formed AccessOverview and emits intent
 * via callbacks (cancel an outgoing request). Data fetching, the four loading
 * states and optimistic mutations belong to the connected wrapper that will wrap
 * this later. Empty sections still render meaningfully here (never a blank void),
 * because "nothing to show" is a real, prop-driven state.
 *
 * Sections, top to bottom:
 *   1. Identity — my org chain (nDSG: who I sit under, always visible), my
 *      lead(s), my roles, and a glanceable per-domain count.
 *   2. Access map — grouped by resource domain; each item shows role + source +
 *      expiry and, when derivable, a "why" chain (AccessExplanation).
 *   3. Outgoing requests — what I've asked for, with status.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, ChevronRight, Inbox, KeyRound, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EmptyState, StaleDataNotice } from '@/components/clinical'
import { PersonAvatar } from '@frontend/components/access/PersonAvatar'
import { AccessExplanation } from './AccessExplanation'
import { ExpiryChip, RoleBadge, SourceChip, StatusPill } from '@frontend/components/access/atoms'
import { relationKey, resourceIcon, resourceTypeKey, roleKey } from '@frontend/components/access/accessMeta'
import type {
  AccessOverview,
  AccessRequest,
  AccessResourceItem,
  ResourceType,
  UserRef,
} from '@frontend/components/access/types'

export interface AccessBoardProps {
  overview: AccessOverview
  /** Withdraw a still-pending outgoing request. Omit to hide the affordance. */
  onCancelRequest?: (requestId: string) => void
  /** Ids of requests whose cancel is in flight (drives per-row pending state). */
  cancellingIds?: string[]
  /**
   * Lazily fetch one item's derivation the FIRST time its "Why?" is opened.
   * When provided, the Why affordance is offered on every item and the chain
   * arrives via a later `item.explanation` update — the connected wrapper fetches
   * GET /me/access/resources/{ref} per tap instead of eagerly for the whole page. Omit
   * (fixtures/preview) to keep the inline `item.explanation` behaviour.
   */
  onExpandWhy?: (item: AccessResourceItem) => void
  /** Retry a single item's explanation fetch after `item.explanationError`.
   *  Omit to hide the retry affordance on a failed "why" (falls back to the
   *  plain error line). */
  onRetryWhy?: (item: AccessResourceItem) => void
  /**
   * Resource TYPES whose reachable-set fetch genuinely failed (not "zero
   * results" — a real fetch error). `resourceQueries.flatMap(q => q.data ??
   * [])` used to fold a failed type into zero silently; this surfaces it so
   * the board can never quietly undercount. Each entry renders a visible
   * per-type error line with `onRetryResourceType`.
   */
  resourceLoadErrors?: ResourceType[]
  onRetryResourceType?: (type: ResourceType) => void
  /**
   * True when the LIVE outgoing-requests feed failed and the list below is
   * the OVERVIEW payload's embedded snapshot instead — real data, but
   * possibly stale. Renders the same visible stale notice used elsewhere
   * (StaleDataNotice) rather than the live failure looking identical to the
   * snapshot being current.
   */
  outgoingRequestsStale?: boolean
  /** Injectable clock for deterministic expiry rendering (dev fixtures/tests). */
  now?: number
  className?: string
}

// Domains surface in clinical priority order; unknown types fall in after these,
// alphabetically. A presentation ordering, not a domain rule.
const DOMAIN_ORDER: ResourceType[] = ['patient', 'session', 'document', 'feature', 'unit']

function domainRank(type: ResourceType): number {
  const i = DOMAIN_ORDER.indexOf(type)
  return i === -1 ? DOMAIN_ORDER.length : i
}

export function AccessBoard({
  overview,
  onCancelRequest,
  cancellingIds,
  onExpandWhy,
  onRetryWhy,
  resourceLoadErrors,
  onRetryResourceType,
  outgoingRequestsStale,
  now,
  className,
}: AccessBoardProps) {
  const { t } = useTranslation()

  const groups = useMemo(() => {
    const byType = new Map<ResourceType, AccessResourceItem[]>()
    for (const item of overview.items) {
      const bucket = byType.get(item.resource.type)
      if (bucket) bucket.push(item)
      else byType.set(item.resource.type, [item])
    }
    return [...byType.entries()].sort((a, b) => {
      const r = domainRank(a[0]) - domainRank(b[0])
      return r !== 0 ? r : a[0].localeCompare(b[0])
    })
  }, [overview.items])

  const countEntries = useMemo(
    () =>
      [...Object.entries(overview.counts)].sort((a, b) => {
        const r = domainRank(a[0]) - domainRank(b[0])
        return r !== 0 ? r : a[0].localeCompare(b[0])
      }),
    [overview.counts],
  )

  return (
    <div className={cn('mx-auto w-full max-w-3xl space-y-6 p-4', className)}>
      <IdentityHeader overview={overview} countEntries={countEntries} />

      {/* Access map */}
      <section className="space-y-4">
        <h2 className="text-[15px] font-semibold text-neutral-900">
          {t('access.board.mapTitle')}
        </h2>
        {/* A resource TYPE that failed to load is a real undercount, never
            folded silently into "zero results" — see resourceLoadErrors doc. */}
        {resourceLoadErrors && resourceLoadErrors.length > 0 && (
          <div className="space-y-1" role="alert">
            {resourceLoadErrors.map((type) => (
              <p key={type} className="text-[13px] text-error">
                {t('access.board.resourceTypeError', {
                  type: t(resourceTypeKey(type), { defaultValue: type }),
                })}{' '}
                {onRetryResourceType && (
                  <button
                    type="button"
                    onClick={() => onRetryResourceType(type)}
                    className="font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    {t('actions.retry', 'Retry')}
                  </button>
                )}
              </p>
            ))}
          </div>
        )}
        {groups.length === 0 ? (
          <EmptyState
            icon={<KeyRound aria-hidden="true" />}
            title={t('access.board.empty.title')}
            description={t('access.board.empty.description')}
          />
        ) : (
          <div className="space-y-5">
            {groups.map(([type, items]) => (
              <DomainSection
                key={type}
                type={type}
                items={items}
                subject={overview.subject}
                onExpandWhy={onExpandWhy}
                onRetryWhy={onRetryWhy}
                now={now}
              />
            ))}
          </div>
        )}
      </section>

      {/* Outgoing requests — StaleDataNotice never blocks the snapshot below
          it; it just says the live feed didn't confirm it's current. */}
      <div className="space-y-2">
        <StaleDataNotice isFetching={false} isError={!!outgoingRequestsStale} />
        <OutgoingRequests
          requests={overview.outgoing_requests}
          onCancel={onCancelRequest}
          cancellingIds={cancellingIds}
        />
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Identity header
// -----------------------------------------------------------------------------

function IdentityHeader({
  overview,
  countEntries,
}: {
  overview: AccessOverview
  countEntries: [string, number][]
}) {
  const { t } = useTranslation()
  const { subject, org_chain, leads, roles } = overview

  return (
    <section className="rounded-[18px] border border-neutral-200 bg-white p-5">
      <div className="flex items-center gap-3">
        <PersonAvatar user={subject} size="lg" />
        <div className="min-w-0">
          <p className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
            {t('access.board.title')}
          </p>
          <h1 className="truncate text-[21px] font-semibold tracking-[-0.01em] text-neutral-900">
            {subject.name}
          </h1>
        </div>
      </div>

      {roles.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {roles.map((r) => (
            <span
              key={r}
              className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-[12px] font-semibold text-primary-700"
            >
              {t(roleKey(r), { defaultValue: r })}
            </span>
          ))}
        </div>
      )}

      {/* Org chain — nDSG: where I sit in the org, always visible. */}
      {org_chain.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-1 text-[13px] text-neutral-600">
          <Building2 className="mr-0.5 h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
          {org_chain.map((node, i) => (
            <span key={node.id} className="inline-flex items-center gap-1">
              {i > 0 && (
                <ChevronRight className="h-3 w-3 shrink-0 text-neutral-300" aria-hidden="true" />
              )}
              <span className={cn(i === org_chain.length - 1 && 'font-semibold text-neutral-900')}>
                {node.label}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Lead(s) */}
      {leads.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
          <span className="inline-flex items-center gap-1 text-neutral-500">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('access.board.leads')}
          </span>
          {leads.map((lead) => (
            <span key={lead.user.id} className="inline-flex items-center gap-1.5">
              <PersonAvatar user={lead.user} size="sm" />
              <span className="font-semibold text-neutral-800">{lead.user.name}</span>
              {lead.relation && (
                <span className="text-neutral-400">
                  {t(relationKey(lead.relation), { defaultValue: lead.relation })}
                </span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Per-domain counts */}
      {countEntries.length > 0 && (
        <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {countEntries.map(([type, count]) => (
            <div key={type} className="rounded-[10px] bg-neutral-50 px-3 py-2.5">
              <dt className="text-[12px] text-neutral-500">
                {t(resourceTypeKey(type), { count, defaultValue: type })}
              </dt>
              <dd className="mt-0.5 text-[21px] font-semibold leading-none tracking-[-0.01em] text-neutral-900 tabular-nums">
                {count}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  )
}

// -----------------------------------------------------------------------------
// Access map
// -----------------------------------------------------------------------------

function DomainSection({
  type,
  items,
  subject,
  onExpandWhy,
  onRetryWhy,
  now,
}: {
  type: ResourceType
  items: AccessResourceItem[]
  subject: UserRef
  onExpandWhy?: (item: AccessResourceItem) => void
  onRetryWhy?: (item: AccessResourceItem) => void
  now?: number
}) {
  const { t } = useTranslation()
  const Icon = resourceIcon(type)
  return (
    <div className="space-y-2">
      <h3 className="inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {t(resourceTypeKey(type), { count: items.length, defaultValue: type })}
        <span className="font-normal normal-case tabular-nums text-neutral-400">
          {items.length}
        </span>
      </h3>
      <ul className="space-y-2">
        {items.map((item) => (
          <AccessItemRow
            key={`${item.resource.type}:${item.resource.id}`}
            item={item}
            subject={subject}
            onExpandWhy={onExpandWhy}
            onRetryWhy={onRetryWhy}
            now={now}
          />
        ))}
      </ul>
    </div>
  )
}

function AccessItemRow({
  item,
  subject,
  onExpandWhy,
  onRetryWhy,
  now,
}: {
  item: AccessResourceItem
  subject: UserRef
  onExpandWhy?: (item: AccessResourceItem) => void
  onRetryWhy?: (item: AccessResourceItem) => void
  now?: number
}) {
  const { t } = useTranslation()
  const [showWhy, setShowWhy] = useState(false)
  const hasWhy = !!item.explanation && item.explanation.length > 0
  // A FAILED fetch is distinct from "not fetched yet" — the latter renders
  // Loading, the former must not, or a nurse whose derivation genuinely
  // errored sees a permanent spinner with no way to know it broke.
  const whyFailed = !!item.explanationError && !hasWhy
  // The Why affordance is offered whenever the chain is already present (fixtures)
  // OR a lazy fetcher is wired (connected board) — the chain then streams in on
  // first open. Loading is shown until item.explanation resolves.
  const canWhy = hasWhy || !!onExpandWhy

  const toggleWhy = () =>
    setShowWhy((open) => {
      const next = !open
      if (next && !hasWhy && onExpandWhy) onExpandWhy(item)
      return next
    })

  return (
    <li className="rounded-[10px] border border-neutral-200 bg-white">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 p-3">
        <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-neutral-900">
          {item.resource.label}
        </span>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <RoleBadge role={item.effective_role} />
          <SourceChip source={item.source} />
          {item.expires_at && <ExpiryChip expiresAt={item.expires_at} now={now} />}
        </div>
        {canWhy && (
          <button
            type="button"
            onClick={toggleWhy}
            aria-expanded={showWhy}
            className="min-h-[44px] shrink-0 rounded-full px-3 text-[13px] font-semibold text-primary transition active:scale-95"
          >
            {showWhy ? t('access.board.hideWhy') : t('access.board.why')}
          </button>
        )}
      </div>
      {showWhy && (
        <div className="border-t border-neutral-100 px-4 pb-4 pt-3">
          {hasWhy ? (
            <AccessExplanation
              subject={subject}
              steps={item.explanation ?? []}
              resourceRole={item.effective_role}
              now={now}
            />
          ) : whyFailed ? (
            <p className="text-[13px] text-error" role="alert">
              {t('access.board.whyError', { defaultValue: 'Could not load why.' })}{' '}
              {onRetryWhy && (
                <button
                  type="button"
                  onClick={() => onRetryWhy(item)}
                  className="font-semibold text-primary underline-offset-2 hover:underline"
                >
                  {t('actions.retry', 'Retry')}
                </button>
              )}
            </p>
          ) : (
            <p className="text-[13px] text-neutral-500" role="status">
              {t('access.board.whyLoading', { defaultValue: 'Loading…' })}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

// -----------------------------------------------------------------------------
// Outgoing requests
// -----------------------------------------------------------------------------

function OutgoingRequests({
  requests,
  onCancel,
  cancellingIds,
}: {
  requests: AccessRequest[]
  onCancel?: (requestId: string) => void
  cancellingIds?: string[]
}) {
  const { t } = useTranslation()

  return (
    <section className="space-y-3">
      <h2 className="text-[15px] font-semibold text-neutral-900">
        {t('access.board.requestsTitle')}
      </h2>
      {requests.length === 0 ? (
        <EmptyState
          icon={<Inbox aria-hidden="true" />}
          title={t('access.board.noRequests.title')}
          description={t('access.board.noRequests.description')}
        />
      ) : (
        <ul className="space-y-2">
          {requests.map((r) => {
            const Icon = resourceIcon(r.resource.type)
            const cancelling = cancellingIds?.includes(r.id)
            return (
              <li
                key={r.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[10px] border border-neutral-200 bg-white p-3"
              >
                <Icon className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-semibold text-neutral-900">
                    {r.resource.label}
                  </p>
                  <p className="text-[12px] text-neutral-500">
                    {/* A request that grants no role (a publish) has no role
                        line — the date stands alone rather than naming an
                        invented level. */}
                    {r.relation && (
                      <>
                        {t('access.board.requestedRole', {
                          role: t(roleKey(r.relation), { defaultValue: r.relation }),
                        })}
                        {' · '}
                      </>
                    )}
                    {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <StatusPill status={r.status} />
                {onCancel && r.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => onCancel(r.id)}
                    disabled={cancelling}
                    className="min-h-[44px] shrink-0 rounded-full px-3 text-[13px] font-semibold text-neutral-500 transition active:scale-95 disabled:opacity-50"
                  >
                    {cancelling ? t('access.board.cancelling') : t('access.board.cancel')}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

